import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import caratulaRegistry from '../Caratula/base/caratulaRegistry';
import SelectorClienteOpcional from './SelectorClienteOpcional';
import { colorPrimarioTramite } from './coloresTramite';
import {
    obtenerDatosCaratulaIndependiente,
    generarCaratulaIndependiente,
    extraerMensajeError,
} from '../../services/caratulaService';

function descargarBlob(data, nombreArchivo) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

const SIN_PASO = 'CERRADO';
const PASO_CLIENTE = 'CLIENTE';
const PASO_VARIANTE = 'VARIANTE';

/**
 * Tarjeta de carátula de la página "Generar Documentos Automáticos": mismo `config.modal` que
 * ya usan las fichas de trámite (los 10 `ModalCaratulaXxx.js`, porque `HojaCaratula` no sabe ni
 * le importa de dónde sale `datos` ni a dónde va `onConfirmar`), pero con un paso extra ANTES
 * de abrir el modal — acá no hay `idTramite`, así que primero hay que resolver si el documento
 * sale prellenado (con un cliente elegido) o en blanco (sin cliente):
 *
 *   click tarjeta → SelectorClienteOpcional → (elegir variante, si el tipo las tiene y no vino
 *   fija por props) → modal con el prefill → generar y descargar (sin adjuntar a ningún trámite).
 *
 * El CUIL elegido viaja SIEMPRE por parámetro hasta `abrirModal`, no por el state: `cuilElegido`
 * se setea y se usa dentro del mismo handler, así que ahí todavía vale lo del render anterior
 * (siempre `null`, porque la tarjeta lo limpia al abrir el primer paso). Leerlo del state hacía
 * que el prefill se pidiera sin cuil y el documento saliera en blanco aunque se hubiera elegido
 * un cliente. El state se conserva solo para el nombre del archivo, que se arma en un render
 * posterior.
 *
 * `variante` es opcional: si se pasa (Pensión, Reajuste de Pensión y Jubilación se usan así,
 * una tarjeta por variante en vez de una con menú adentro — mismo criterio que los 3 pactos),
 * el paso de elegir variante se salta directo. Reajuste (de Jubilación) sigue sin pasarla y
 * conserva el menú interno.
 *
 * Los dos primeros pasos viven en el MISMO Dialog (una máquina de estados simple, `paso`), en
 * vez de un Menu anclado a un botón: al cerrar el paso anterior el elemento que lo disparó ya
 * no existe, así que anclar un Menu ahí sería frágil.
 */
export default function BotonGenerarDocumentoIndependiente({ tipo, variante, etiqueta, descripcion, icono = 'bi-file-earmark-text' }) {
    const config = caratulaRegistry[tipo];

    const [paso, setPaso] = useState(SIN_PASO);
    const [cuilElegido, setCuilElegido] = useState(null);
    const [cargandoDatos, setCargandoDatos] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [datos, setDatos] = useState(null);
    const [errorCarga, setErrorCarga] = useState(null);

    if (!config) {
        return null;
    }

    const ModalCaratula = config.modal;

    const abrirModal = async (varianteElegida, cuil) => {
        setPaso(SIN_PASO);
        setErrorCarga(null);
        // Sin esto el modal se abre hidratado con el documento anterior: `modalAbierto` pasa a
        // true antes de que resuelva el fetch y el efecto de HojaCaratula depende de `datos`.
        setDatos(null);
        setModalAbierto(true);
        setCargandoDatos(true);
        try {
            const datosPrefill = await obtenerDatosCaratulaIndependiente(config.urlBase, cuil, varianteElegida);
            setDatos(datosPrefill);
        } catch (err) {
            console.error(`❌ Error obteniendo los datos de la Carátula ${config.etiqueta}:`, err);
            setErrorCarga('No se pudieron cargar los datos de la Carátula. Intente nuevamente.');
            setModalAbierto(false);
            toast.error('No se pudieron cargar los datos de la Carátula');
        } finally {
            setCargandoDatos(false);
        }
    };

    const handleCuilResuelto = (cliente) => {
        const cuil = cliente?.cuil || null;
        setCuilElegido(cuil);
        if (variante) {
            abrirModal(variante, cuil);
        } else if (config.variantes) {
            setPaso(PASO_VARIANTE);
        } else {
            abrirModal(undefined, cuil);
        }
    };

    const handleConfirmar = async (datosEditados) => {
        setGenerando(true);
        try {
            // Con cliente elegido, el backend guarda en él mail, fecha de nacimiento, claves y
            // domicilio completados en el modal. Sin cliente no hay dónde guardar nada.
            const response = await generarCaratulaIndependiente(config.urlBase, datosEditados, cuilElegido);
            descargarBlob(response.data, config.nombreArchivo(datosEditados, cuilElegido || 'sin-cliente'));
            toast.success(cuilElegido
                ? `Datos del cliente guardados. ${config.mensajeDescarga}`
                : config.mensajeDescarga);
            setModalAbierto(false);
        } catch (err) {
            const mensaje = await extraerMensajeError(err);
            toast.error(mensaje);
            err.mensajeUsuario = mensaje;
            throw err;
        } finally {
            setGenerando(false);
        }
    };

    return (
        <>
            <div
                className={`acceso-card acceso-card-${config.urlBase}`}
                onClick={() => { setCuilElegido(null); setPaso(PASO_CLIENTE); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCuilElegido(null);
                        setPaso(PASO_CLIENTE);
                    }
                }}
            >
                <i className={`bi ${icono} acceso-card-icon`}></i>
                <div className="acceso-card-body">
                    <h5>{etiqueta || config.etiqueta}</h5>
                    <p>{descripcion || `Generar Carátula ${config.etiqueta}`}</p>
                </div>
                <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
            </div>

            {/* Paso 1 (cliente) y paso 2 (variante, si el tipo las tiene). El header es claro,
                con solo un tinte suave del color de la tarjeta (no un bloque sólido): alcanza
                para identificar el trámite sin resultar invasivo en un modal chico. */}
            <Dialog
                open={paso !== SIN_PASO}
                onClose={() => setPaso(SIN_PASO)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}
            >
                <Box
                    sx={{
                        background: `${colorPrimarioTramite(config.urlBase)}12`,
                        borderBottom: `1px solid ${colorPrimarioTramite(config.urlBase)}33`,
                        px: 3, py: 2.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: `${colorPrimarioTramite(config.urlBase)}1F`,
                                color: colorPrimarioTramite(config.urlBase),
                                fontSize: '1.3rem',
                            }}
                        >
                            <i className={`bi ${icono}`}></i>
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
                                {etiqueta || config.etiqueta}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {paso === PASO_VARIANTE ? '¿Qué variante generamos?' : '¿Generamos para un cliente del sistema?'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setPaso(SIN_PASO)} size="small" sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 3 }}>
                    {paso === PASO_CLIENTE && (
                        <SelectorClienteOpcional onContinuar={handleCuilResuelto} color={colorPrimarioTramite(config.urlBase)} />
                    )}
                    {paso === PASO_VARIANTE && (
                        <ul className="list-group">
                            {config.variantes.map((v) => (
                                <li
                                    key={v.value}
                                    className="list-group-item list-group-item-action"
                                    role="button"
                                    onClick={() => abrirModal(v.value, cuilElegido)}
                                >
                                    <div className="fw-semibold" style={{ color: colorPrimarioTramite(config.urlBase) }}>{v.titulo}</div>
                                    <small className="text-muted">{v.detalle}</small>
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogContent>
            </Dialog>

            {errorCarga && (
                <div className="alert alert-warning py-2 px-3 mt-2 mb-0 rounded-3" style={{ fontSize: '0.85rem' }}>
                    {errorCarga}
                </div>
            )}

            {/* Paso 3: la misma hoja A4 que usan las fichas de trámite */}
            <ModalCaratula
                abierto={modalAbierto}
                cargandoDatos={cargandoDatos}
                datos={datos}
                generando={generando}
                onCancelar={() => setModalAbierto(false)}
                onConfirmar={handleConfirmar}
            />
        </>
    );
}
